import React, { useCallback, useEffect, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

import { PayuConfig } from '../../types/subscriptions';
import { useSetupCard } from '../../api/subscriptions';
import { SUCCESS_SNACKBAR } from '../../consts';
import styles from './paymentCardForm.module.css';

interface PaymentCardFormProps {
  payu: PayuConfig;
  /** Called after the card was stored without a 3DS redirect. */
  onSuccess?: () => void;
  submitLabel?: string;
  helperText?: React.ReactNode;
  /** Rendered above the card field - e.g. a plan picker, so the gate is one
   *  form with one submit button. */
  header?: React.ReactNode;
  /** Runs when the single submit button is pressed, before the card is
   *  tokenized. Return false to abort (the caller shows its own error). */
  beforeSubmit?: () => Promise<boolean>;
  /** Disable the submit button (e.g. while the header form is invalid). */
  submitDisabled?: boolean;
}

const SDK_URL_PROD = 'https://secure.payu.com/javascript/sdk';
const SDK_URL_SANDBOX = 'https://secure.snd.payu.com/javascript/sdk';

const loadSdk = (sandbox: boolean): Promise<void> => {
  const src = sandbox ? SDK_URL_SANDBOX : SDK_URL_PROD;
  if (window.PayU) return Promise.resolve();

  const waitForGlobal = (): Promise<void> =>
    new Promise((resolve, reject) => {
      let tries = 0;
      const timer = setInterval(() => {
        if (window.PayU) {
          clearInterval(timer);
          resolve();
        } else if (++tries > 100) {
          clearInterval(timer);
          reject(new Error('PayU SDK failed to load'));
        }
      }, 100);
    });

  if (document.querySelector(`script[data-payu-sdk="${src}"]`)) {
    return waitForGlobal();
  }

  const script = document.createElement('script');
  script.src = src;
  script.async = true;
  script.dataset.payuSdk = src;
  document.body.appendChild(script);
  return waitForGlobal();
};

export const PaymentCardForm: React.FC<PaymentCardFormProps> = ({
  payu,
  onSuccess,
  submitLabel,
  helperText,
  header,
  beforeSubmit,
  submitDisabled = false,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { setupCard, loading: settingUpCard } = useSetupCard();
  const [running, setRunning] = useState(false);
  const submitting = settingUpCard || running;

  const cardRef = useRef<HTMLDivElement>(null);
  const secureFormRef = useRef<any>(null);
  const payuRef = useRef<any>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadSdk(payu.sandbox)
      .then(() => {
        if (cancelled || !window.PayU || !cardRef.current) return;
        const instance = window.PayU(payu.pos_id);
        const secureForms = instance.secureForms();
        const card = secureForms.add('card', {
          style: { basic: { fontSize: '16px' } },
        });
        card.render('#payu-card');
        payuRef.current = instance;
        secureFormRef.current = card;
        setReady(true);
      })
      .catch(() => {
        if (!cancelled) setError(t('The payment form could not be loaded. Please try again.'));
      });
    return () => {
      cancelled = true;
    };
  }, [payu.pos_id, payu.sandbox, t]);

  const handleSubmit = useCallback(async () => {
    if (!payuRef.current || submitting) return;
    setError(null);
    setRunning(true);
    try {
      if (beforeSubmit && !(await beforeSubmit())) return;

      // 'MULTI' - the token must be reusable; a 'SINGLE' (disposable) token is
      // rejected server-side with RECURRING_WITH_DISPOSABLE_TOKEN.
      let result: { status: string; body?: { token?: string } };
      try {
        result = await payuRef.current.tokenize('MULTI');
      } catch {
        setError(t('The card details could not be verified. Check them and try again.'));
        return;
      }
      if (result.status !== 'SUCCESS' || !result.body?.token) {
        setError(t('The card details could not be verified. Check them and try again.'));
        return;
      }

      const setup = await setupCard(result.body.token);
      if (!setup || setup.status === 'error') {
        setError(t('The card could not be saved. Please try a different card.'));
        return;
      }
      if (setup.status === '3ds' && setup.redirect_uri) {
        window.location.assign(setup.redirect_uri);
        return;
      }
      enqueueSnackbar(t('Card saved.'), SUCCESS_SNACKBAR);
      onSuccess?.();
    } finally {
      setRunning(false);
    }
  }, [beforeSubmit, enqueueSnackbar, onSuccess, setupCard, submitting, t]);

  return (
    <Box className={styles.container}>
      {header}
      {helperText && (
        <Typography variant="body2" color="text.secondary">
          {helperText}
        </Typography>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      <Box id="payu-card" ref={cardRef} className={styles.cardField} />
      {!ready && !error && (
        <Box className={styles.loading}>
          <CircularProgress size={20} />
        </Box>
      )}
      <Button
        variant="contained"
        onClick={handleSubmit}
        disabled={!ready || submitting || submitDisabled}
        startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
      >
        {submitLabel ?? t('Save card')}
      </Button>
      <Typography variant="caption" color="text.secondary">
        {t('Card data is entered directly into PayU and never reaches our servers.')}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {t('We authorise a small amount to verify the card and refund it immediately.')}
      </Typography>
    </Box>
  );
};
